<template>
  <div
    class="element"
    :class="[
      elementClass,
      {
        'checkbox custom-control custom-checkbox': isCheckbox,
        'radio custom-control custom-radio': isRadio,
        datepicker: isDatePicker,
        'has-error': errors.has(name),
        'has-label': !!label,
      },
    ]"
  >
    <!-- TEXT -->
    <template v-if="isText">
      <label v-if="label" :for="id" :class="labelClass">
        {{ label }}
        <span v-if="validate">*</span>
      </label>
      <input
        ref="input"
        v-model="input"
        :type="type"
        :name="name"
        :id="id"
        :class="[
          inputClass || 'form-control',
          { 'is-invalid': errors.has(name) || !!errorText },
        ]"
        :style="inputStyle"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        v-validate.disable="validate"
        :data-vv-as="validateAs"
        :autocomplete="autocomplete"
        :maxlength="maxlength"
        @keydown="$emit('keydown', input)"
        @keyup.enter="$emit('enter')"
        @keyup="$emit('keyup', input)"
        @click="$emit('click')"
        @focus="$emit('focus')"
        @blur="$emit('blur')"
        @change="$emit('change', input)"
        @keydown.enter.prevent
      />
    </template>

    <!-- TEXTAREA -->
    <template v-if="isTextarea">
      <label v-if="label" :for="name" :class="labelClass">
        {{ label }}
        <span v-if="validate">*</span>
      </label>
      <textarea
        ref="input"
        v-model="input"
        :name="name"
        :id="id"
        :class="[
          inputClass || 'form-control',
          { 'is-invalid': errors.has(name) || !!errorText },
        ]"
        :placeholder="placeholder"
        :style="[inputStyle, { height: `${textareaHeight}px` }]"
        :disabled="disabled"
        :readonly="readonly"
        v-validate.disable="validate"
        :data-vv-as="validateAs"
        @keydown="$emit('keydown')"
        @keyup.enter="$emit('enter')"
        @keyup="$emit('keyup')"
        @click="$emit('click')"
        @focus="$emit('focus')"
        @blur="$emit('blur')"
        @change="$emit('change', input)"
        @keydown.enter.prevent
      />
    </template>

    <!-- SELECT -->
    <template v-if="isSelect">
      <label v-if="label" :for="name" :class="labelClass">
        {{ label }}
        <span v-if="validate">*</span>
      </label>
      <div
        class="select-wrapper"
        :class="{ 'is-invalid': errors.has(name) || !!errorText }"
      >
        <select
          ref="input"
          v-model="input"
          :type="type"
          :name="name"
          :id="id"
          class="custom-select"
          :class="[
            inputClass,
            { 'is-invalid': errors.has(name) || !!errorText },
          ]"
          :style="inputStyle"
          :disabled="disabled"
          :readonly="readonly"
          v-validate.disable="validate"
          :data-vv-as="validateAs"
          @focus="$emit('focus')"
          @blur="$emit('blur')"
          @change="$emit('change', input)"
        >
          <option v-if="placeholder" value disabled>{{ placeholder }}</option>
          <option v-if="!hideSelectEmptyOption" value>
            {{ selectEmptyOption }}
          </option>
          <option v-for="(option, i) in options" :key="i" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
    </template>

    <!-- CHECKBOX -->
    <template v-if="isCheckbox">
      <input
        ref="input"
        type="checkbox"
        v-model="input"
        :value="checkboxValue"
        :name="name"
        :id="id"
        class="custom-control-input"
        :class="[inputClass, { 'is-invalid': errors.has(name) || !!errorText }]"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        v-validate.disable="validate"
        :data-vv-as="validateAs"
        @click="$emit('click')"
      />
      <label
        v-if="label"
        :for="id"
        class="custom-control-label"
        :class="labelClass"
        v-html="label"
      ></label>
    </template>

    <!-- RADIO -->
    <template v-if="isRadio">
      <input
        ref="input"
        type="radio"
        v-model="input"
        :value="radioValue"
        :name="name"
        :id="id"
        class="custom-control-input"
        :class="[inputClass, { 'is-invalid': errors.has(name) || !!errorText }]"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        v-validate.disable="validate"
        :data-vv-as="validateAs"
        @click="$emit('click')"
      />
      <label
        v-if="label"
        :for="id"
        class="custom-control-label"
        :class="labelClass"
        v-html="label"
      ></label>
    </template>

    <!-- DATEPICKER -->
    <template v-if="isDatePicker">
      <label v-if="label" :for="id" :class="labelClass">
        {{ label }}
        <span v-if="validate">*</span>
      </label>
      <date-picker
        ref="datepicker"
        v-model="input"
        :id="id"
        :name="`${name}_input`"
        :class="[
          inputClass || 'form-control',
          { 'is-invalid': errors.has(name) || !!errorText },
          { disabled: !!disabled },
        ]"
        :disabled="disabled"
        format="MM/dd/yyyy"
        :clear-button="true"
        clear-button-icon="icon-plus"
        :typeable="true"
        @selected="$emit('selected')"
      ></date-picker>
      <span class="datepicker-icon" @click="$refs.datepicker.showCalendar()">
        <icon icon="calendar" />
      </span>
      <input
        v-if="input"
        type="hidden"
        :name="name"
        :value="$moment(input).format('YYYY-MM-DD')"
      />
    </template>

    <div v-if="errorText" class="invalid-feedback" v-html="errorText"></div>
    <div
      v-else
      v-for="(error, i) in errors.collect(name)"
      :key="i"
      class="invalid-feedback"
    >
      {{ error }}
    </div>

    <div v-if="helpText" class="help-text" v-html="helpText"></div>
  </div>
</template>

<script>
import InputFieldProps from "mixins/InputFieldProps";
import DatePicker from "vuejs-datepicker";

export default {
  inject: ["$validator"],
  mixins: [InputFieldProps],
  components: { DatePicker },
  props: {
    value: { required: true }, // v-model attribute from parent component
    textareaHeight: { type: [String, Number], default: 100 }, // textarea height in px
    options: { type: Array, default: () => [] }, // array of options for select elements
    selectEmptyOption: { type: String, default: "(none)" },
    hideSelectEmptyOption: { type: Boolean, default: false },
    checkboxValue: { type: [String, Number], required: false }, // the value attribute for checkbox elements
    radioValue: { type: [String, Number], required: false }, // the value attribute for radio elements
    inputStyle: { type: String, required: false }, // optional style applied to the input element
	  autocomplete: { type: String, default: null} ,
    maxlength: {type: [String, Number], default: 255},
  },
  data() {
    return {
      input: null,
    };
  },
  computed: {
    isText() {
      return ["text", "email", "password", "number"].includes(this.type);
    },
    isTextarea() {
      return this.type === "textarea";
    },
    isSelect() {
      return this.type === "select";
    },
    isCheckbox() {
      return this.type === "checkbox";
    },
    isRadio() {
      return this.type === "radio";
    },
    isDatePicker() {
      return this.type === "datepicker";
    },
  },
  watch: {
    value(val) {
      this.input = val;
    },
    input(val) {
      if (this.isCheckbox) {
        this.$emit("input", val, this.checkboxValue);
      } else if (this.isRadio) {
        this.$emit("input", val, this.radioValue);
      } else {
        this.$emit("input", val);
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.input = this.value;
    });
  },
};
</script>
